import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TransactionB2C, TransactionStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { TransactionsService } from '@/services/transactionsService';
import { ArrowUpRight, ArrowDownRight, Printer, Filter, Search, RefreshCw, DollarSign, Clock, List } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { DateRange } from 'react-day-picker';
import { subDays } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { Progress } from '@/components/ui/progress';

const Transactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<TransactionB2C[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('all');
  const [date, setDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    totalCount: 0,
    completedCount: 0,
    pendingCount: 0,
    failedCount: 0,
    refundedCount: 0,
    totalAmount: 0,
    pendingAmount: 0,
    refundedAmount: 0,
  });

  // Fetch transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        
        const filters = {
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? (statusFilter as TransactionStatus) : undefined,
          dateFrom: date?.from,
          dateTo: date?.to,
        };
        
        const [transactionsData, statsData] = await Promise.all([
          TransactionsService.getTransactions(filters),
          TransactionsService.getTransactionStats({ dateFrom: date?.from, dateTo: date?.to })
        ]);
        
        setTransactions(transactionsData);
        setSummaryStats(statsData);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        toast.error('Falha ao carregar transações: ' + (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [searchTerm, statusFilter, date]);





  // Format currency
  const formatCurrency = (amount: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  // Get summary data (now using the stats from the service)
  const getSummaryData = () => {
    return summaryStats;
  };

  // Filter transactions based on active tab (search and date filtering is now handled by the service)
  const getFilteredTransactions = () => {
    return transactions.filter((transaction) => {
      const matchesTab = 
        activeTab === 'all' || 
        (activeTab === 'income' && transaction.status === 'completed') ||
        (activeTab === 'pending' && transaction.status === 'pending') ||
        (activeTab === 'refunded' && transaction.status === 'refunded');

      return matchesTab;
    });
  };

  const summaryData = getSummaryData();
  const filteredTransactions = getFilteredTransactions();

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Transações</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Background Image */}
        <div 
          className="relative overflow-hidden" 
          style={{ 
            backgroundColor: '#1F1746',
            backgroundImage: 'url(/elemento-01.svg)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Transações</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie e monitore todas as transações financeiras da sua organização
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Espaço para botões futuros se necessário */}
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <ArrowUpRight className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total Recebido</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(summaryData.totalAmount, 'BRL')}</div>
                  </div>
                  <p className="text-xs text-gray-500">{summaryData.completedCount} transações completadas</p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-yellow-100 rounded-md">
                        <Clock className="h-4 w-4 text-yellow-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Pendente</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(summaryData.pendingAmount, 'BRL')}</div>
                  </div>
                  <p className="text-xs text-gray-500">{summaryData.pendingCount} transações pendentes</p>
                </CardContent>
              </Card>
              
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <ArrowDownRight className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Reembolsado</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(summaryData.refundedAmount, 'BRL')}</div>
                  </div>
                  <p className="text-xs text-gray-500">{summaryData.refundedCount} transações reembolsadas</p>
                </CardContent>
              </Card>
              
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <List className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Transações</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{summaryData.totalCount}</div>
                  </div>
                  <Progress value={summaryData.totalCount > 0 ? (summaryData.completedCount / summaryData.totalCount) * 100 : 0} className="h-1.5" />
                  <div className="text-xs text-gray-500 mt-2">
                    {summaryData.completedCount} de {summaryData.totalCount} completadas
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Card */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 text-muted-foreground -translate-y-1/2" />
                    <Input
                      placeholder="Buscar transações..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 bg-white"
                    />
                  </div>
                  
                  <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex gap-1 h-10 bg-white">
                        <Filter size={16} />
                        <span>Filtros</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h4 className="font-extrabold">Período</h4>
                          <Calendar
                            mode="range"
                            selected={date}
                            onSelect={setDate}
                            className="rounded-md border"
                          />
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-extrabold">Status</h4>
                          <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger>
                              <SelectValue placeholder="Filtrar por status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos os status</SelectItem>
                              <SelectItem value="pending">Pendente</SelectItem>
                              <SelectItem value="completed">Concluído</SelectItem>
                              <SelectItem value="failed">Falhou</SelectItem>
                              <SelectItem value="refunded">Reembolsado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-between">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setStatusFilter('all');
                              setDate(undefined);
                            }}
                          >
                            Resetar
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => setIsFilterOpen(false)}
                          >
                            Aplicar Filtros
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
                  <TabsList>
                    <TabsTrigger value="all">Todas</TabsTrigger>
                    <TabsTrigger value="income">Recebidas</TabsTrigger>
                    <TabsTrigger value="pending">Pendentes</TabsTrigger>
                    <TabsTrigger value="refunded">Reembolsadas</TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                                         <TableHeader>
                       <TableRow>
                         <TableHead>ID</TableHead>
                         <TableHead>Cliente</TableHead>
                         <TableHead>Origem</TableHead>
                         <TableHead>Valor</TableHead>
                         <TableHead>Gateway</TableHead>
                         <TableHead>Data</TableHead>
                         <TableHead>Status</TableHead>
                       </TableRow>
                     </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                            <div className="mt-2 text-sm text-muted-foreground">Carregando transações...</div>
                          </TableCell>
                        </TableRow>
                      ) : filteredTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10">
                            <div className="text-sm text-muted-foreground">Nenhuma transação encontrada</div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredTransactions.map((transaction) => (
                          <TableRow 
                            key={transaction.id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/transactions/${transaction.id}`)}
                          >
                            <TableCell className="font-extrabold">{transaction.display_id}</TableCell>
                            <TableCell className="max-w-[150px] truncate">
                              {(transaction as any).customer_contacts ? 
                                `${(transaction as any).customer_contacts.first_name} ${(transaction as any).customer_contacts.last_name || ''}`.trim() :
                                '-'
                              }
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate">{transaction.source}</TableCell>
                            <TableCell className="font-extrabold">{formatCurrency(Number(transaction.price))}</TableCell>
                                                         <TableCell>
                               {transaction.payment_gateway || '-'}
                             </TableCell>
                            <TableCell>
                              {format(new Date(transaction.sale_date), 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                transaction.status === 'completed' ? 'success' :
                                transaction.status === 'pending' ? 'warning' :
                                transaction.status === 'failed' ? 'destructive' :
                                'info'
                              }>
                                {transaction.status === 'completed' && 'Concluído'}
                                {transaction.status === 'pending' && 'Pendente'}
                                {transaction.status === 'failed' && 'Falhou'}
                                {transaction.status === 'refunded' && 'Reembolsado'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Mostrando {filteredTransactions.length} de {transactions.length} transações
                </div>
              </CardContent>
            </Card>
            <div className="h-16" /> {/* Bottom spacing */}
          </div>
        </div>
    </>
  );
};

export default Transactions;